/**
 * Loading States Design System Configuration
 * Single source of truth for all loading indicators
 *
 * This ensures consistent UX across the entire application
 */

import { colors, spacing, radius, transitions } from '../../theme/tokens';

export const loadingConfig = {
  // ========================================
  // ANIMATION SETTINGS
  // ========================================
  animation: {
    // Skeleton shimmer effect
    shimmer: {
      duration: '1.5s',
      easing: 'ease-in-out',
      // Gradient for shimmer effect
      gradient: `linear-gradient(
        90deg,
        ${colors.neutral[100]} 0%,
        ${colors.neutral[200]} 50%,
        ${colors.neutral[100]} 100%
      )`,
    },

    // Spinner rotation
    spinner: {
      duration: '0.8s',
      easing: 'linear',
    },

    // Fade in when content loads
    fadeIn: {
      duration: transitions.duration.fast,
      easing: transitions.easing.standard,
    },

    // Pulse effect (alternative to shimmer)
    pulse: {
      duration: '1.2s',
      easing: 'ease-in-out',
    },
  },

  // ========================================
  // COLORS
  // ========================================
  colors: {
    // Skeleton backgrounds
    skeleton: {
      base: colors.neutral[100],      // Base gray
      highlight: colors.neutral[200],  // Shimmer highlight
      contrast: colors.neutral[300],   // For borders/separation
    },

    // Spinner colors
    spinner: {
      primary: colors.primary.main,    // #1324a1
      secondary: colors.neutral[400],  // Background track
    },

    // Progress bars
    progress: {
      bar: colors.primary.main,        // Active progress
      track: colors.neutral[200],      // Background track
      success: colors.success.main,    // When complete
      error: colors.error.main,        // On failure
    },

    // Overlay backgrounds
    overlay: {
      background: 'rgba(255, 255, 255, 0.9)',  // Slightly opaque
      backdrop: 'rgba(0, 0, 0, 0.1)',          // Subtle darkening
    },
  },

  // ========================================
  // SIZING
  // ========================================
  sizes: {
    // Spinner sizes
    spinner: {
      xs: 14,   // Inline text (e.g., SaveIndicator)
      sm: 20,   // Small buttons
      md: 24,   // Default buttons
      lg: 40,   // Prominent actions
      xl: 60,   // Full-page (rare)
    },

    // Skeleton heights
    skeleton: {
      text: {
        xs: 12,   // Caption text
        sm: 16,   // Body text
        md: 20,   // Subheadings
        lg: 28,   // Headings
        xl: 36,   // Page titles
      },
      avatar: {
        sm: 32,
        md: 48,
        lg: 64,
      },
      card: {
        sm: 120,
        md: 200,
        lg: 300,
      },
      button: {
        sm: 32,
        md: 40,
        lg: 48,
      },
    },

    // Progress bar heights
    progress: {
      thin: 4,      // Subtle progress (e.g., auto-save)
      normal: 8,    // Standard progress
      thick: 12,    // Prominent progress (e.g., form completion)
    },
  },

  // ========================================
  // BORDER RADIUS (Consistency)
  // ========================================
  radius: {
    text: radius.sm,       // 4px - Text skeletons
    card: radius.card,     // 12px - Cards
    button: radius.button, // 8px - Buttons
    avatar: radius.full,   // 50% - Circular
    progress: radius.full, // 9999px - Progress bars
  },

  // ========================================
  // TIMING (When to show)
  // ========================================
  timing: {
    // Don't show loading indicator if operation completes in < 300ms
    delayThreshold: 300,

    // Minimum time to show indicator (prevents flash)
    minimumDisplay: 500,

    // Skeleton screen timeout (fallback to spinner if > 10s)
    skeletonTimeout: 10000,
  },

  // ========================================
  // ACCESSIBILITY
  // ========================================
  a11y: {
    // ARIA labels (Hebrew)
    labels: {
      loading: 'טוען...',
      uploading: 'מעלה קובץ...',
      processing: 'מעבד...',
      saving: 'שומר...',
      completed: 'הושלם',
      failed: 'נכשל',
    },

    // Reduced motion support
    respectMotion: true, // Always check prefers-reduced-motion
  },

  // ========================================
  // USAGE GUIDE (Semantic)
  // ========================================
  usage: {
    skeleton: {
      // When to use skeleton screens
      when: [
        'Loading lists of items (orgs, documents)',
        'Loading page layouts (org page, submitted page)',
        'Initial page load with known structure',
        'Loading tables with multiple rows',
      ],
      avoidWhen: [
        'File uploads (use progress bar)',
        'Quick operations < 1s',
        'Unknown/dynamic layouts',
      ],
    },

    spinner: {
      // When to use inline spinners
      when: [
        'Button actions (submitting form)',
        'Inline operations (saving field)',
        'Small contextual loading',
        'Auth operations',
      ],
      avoidWhen: [
        'Full page loads',
        'List/table loading',
        'Operations > 2 seconds (use skeleton)',
      ],
    },

    progress: {
      // When to use progress bars
      when: [
        'File uploads',
        'PDF processing',
        'Multi-step forms',
        'Batch operations',
      ],
      avoidWhen: [
        'Unknown duration',
        'Instant operations',
        'Multiple concurrent operations',
      ],
    },

    overlay: {
      // When to use blocking overlay (RARE!)
      when: [
        'Critical blocking operations only',
        'Payment processing',
        'Final submission (if can\'t be optimistic)',
      ],
      avoidWhen: [
        'Most operations!',
        'Background saves',
        'Non-critical actions',
      ],
    },
  },
};

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Get shimmer animation keyframes
 */
export const getShimmerAnimation = () => `
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
`;

/**
 * Get spinner rotation keyframes
 */
export const getSpinnerAnimation = () => `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

/**
 * Get pulse animation keyframes
 */
export const getPulseAnimation = () => `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

/**
 * Check if operation should show loading indicator
 * @param {number} duration - Expected operation duration (ms)
 * @returns {boolean}
 */
export const shouldShowLoading = (duration) => {
  return duration > loadingConfig.timing.delayThreshold;
};

/**
 * Get appropriate loading component based on context
 * @param {string} context - 'page' | 'list' | 'button' | 'upload' | 'overlay'
 * @returns {string} - Component name to use
 */
export const getLoadingComponent = (context) => {
  const map = {
    page: 'SkeletonLayout',
    list: 'SkeletonCard',
    table: 'SkeletonTable',
    form: 'SkeletonForm',
    button: 'InlineSpinner',
    upload: 'ProgressBar',
    overlay: 'LoadingOverlay',
  };

  return map[context] || 'InlineSpinner';
};

export default loadingConfig;
